import {
    NX_PLUS_TIER_GUID,
    NX_LEGACY_TIER_GUID,
    NX_DEFAULT_LEGACY_TIER_GUID,
    PAYMENT_TYPES,
    NX_FREE_TIER_GUID,
    NX_LEGACY_PRIMIUM_TIER_GUID,
    NX_LEGACY_CLASSIC_TIER_GUID,
    NX_LEGACY_LITE_TIER_GUID,
    NX_FRESH_BASIC_TIER_OPERATOR,
    NX_IN_APP_ANDROID_OPERATORS,
    NX_IN_APP_APPLE_OPERATORS,
} from '@intigral/constant';
import { SUBSCRIPTION_STATUS, SUBSCRIPTION_TIER_ACTIVITY, USER_SEGMENT } from '@intigral/constant/common';
import {
    ICountryConfig,
    IFreeVsPaidConfig,
    IPaymentMethodConfig,
    ISubscriptionConfig,
    ITierConfig,
    IUserFullObject,
} from '@intigral/types';
import { isBeforeOrEqualDate } from '../helpers/datetime';

/**
 * Function to identify if the user subscribed to free tier or not.
 * @param {IUserFullObject} user User object
 * @returns {boolean} Returns true in case the user subscribed to free tier.
 */
export const isFreeTier = (user: IUserFullObject, tierGuid = ''): boolean => {
    const isGuestUser = (user as IUserFullObject & { isGuest?: boolean })?.isGuest === true;
    if (isGuestUser) {
        return true;
    }

    let tierGuidVal = tierGuid;
    if (user?.tierStatus?.tierGuid) {
        tierGuidVal = user.tierStatus.tierGuid;
    } else if (user?.tierConfig?.tierGuid) {
        tierGuidVal = user.tierConfig.tierGuid;
    }
    return tierGuidVal.toUpperCase().includes('FREE');
};

/**
 * Function to identify if the user fresh basic tier user with no payment method. (not consider gift card user)
 * @param {IUserFullObject} operator User operator
 * @returns {boolean} Returns true in case the user fresh basic user.
 */
export const isFreshBasicOperator = (operator: string): boolean => {
    return (process.env['NX_FRESH_BASIC_TIER_OPERATOR'] || NX_FRESH_BASIC_TIER_OPERATOR)
        .toUpperCase()
        .includes(operator.toUpperCase());
};

/**
 * Function to identify if the user subscribed to free tier or inActive or not.
 * @param {IUserFullObject} user User object
 * @returns {boolean} Returns true in case the user subscribed to free tier.
 */
export const isFreeOrInactive = (user: IUserFullObject): boolean => {
    if (user?.tierStatus === undefined && user?.subscriptionStatus?.[0]?.active === true) {
        return true;
    }

    return (
        (user?.tierStatus?.tierGuid ?? user?.tierConfig?.tierGuid ?? '').toUpperCase().includes('FREE') ||
        !user?.tierStatus?.active
    );
};

/**
 * Function to get the GUID for plus tier.
 * @returns {string} GUID for tier
 */
export const getPlusTierGuid = (): string => {
    const plusTierGuid = process.env['NX_PLUS_TIER_GUID'] || NX_PLUS_TIER_GUID;
    return plusTierGuid.toUpperCase();
};

/**
 * Function to get the GUID for plus tier.
 * @returns {string} GUID for tier
 */
export const getFreeTierGuid = (): string => {
    const plusTierGuid = process.env['NX_FREE_TIER_GUID'] || NX_FREE_TIER_GUID;
    return plusTierGuid.toUpperCase();
};

/**
 * Function to get the GUID for plus tier.
 * @returns {string} GUID for tier
 */
export const getCurrentSubscribedTierGuid = (user: IUserFullObject): string => {
    return (user?.tierStatus?.tierGuid ?? user?.tierConfig?.tierGuid ?? '').toUpperCase();
};



/**
 * Get legacy tiers GUIDs
 * @returns string[]
 */
export const getLegacyTiers = (): string[] => {
    return ['CLASSIC_TIER', 'LITE_TIER', 'PREMIUM_TIER'];
};

/**
 *  Get Default Legacy Tier
 * @returns {string}
 */
export const getDefaultLegacyTier = (): string => {
    const defaultLegacyTier = process.env['NX_DEFAULT_LEGACY_TIER_GUID'] || NX_DEFAULT_LEGACY_TIER_GUID;
    return defaultLegacyTier.toUpperCase();
};

/**
 * Function to get the plus tier config object for a selected payment method.
 * @param {IPaymentMethodConfig} paymentConfig Payment method config object for selected payment method
 * @returns {ITierConfig|null} The plus tier config object or null
 */
export const getPlusTierConfig = (paymentConfig: IPaymentMethodConfig): ITierConfig | null => {
    for (const tier of paymentConfig?.tiers || []) {
        if ((tier.tierConfig?.tierGuid || '').toUpperCase() === getPlusTierGuid()) {
            return tier.tierConfig as ITierConfig;
        }
    }
    return null;
};

/**
 * Function to get addons for a particular tier
 * @param {IPaymentMethodConfig} paymentConfig The payment config for selected mayment method.
 * @param {string|undefined} tierGuid The tier guid for which addons are needed. If not provided, plus tier will be selected.
 * @returns {ISubscriptionConfig[]} Array of addon subcription details.
 */
export const getTierAddons = (paymentConfig: IPaymentMethodConfig, tierGuid?: string): ISubscriptionConfig[] => {
    if (!tierGuid) {
        tierGuid = getPlusTierGuid();
    }
    for (const tier of paymentConfig?.tiers || []) {
        if ((tier.tierConfig?.tierGuid || '').toUpperCase() === tierGuid.toUpperCase()) {
            return tier.subscriptions;
        }
    }
    return [];
};

/**
 * Function to get the selecte payment method config
 * @param {IPaymentMethodConfig[]} paymentMethods Array of payment method config.
 * @param {string|undefined} operator The operator for which payment details are needed.
 * @returns {IPaymentMethodConfig} selected payment method config
 */
export const getPaymentConfig = (
    paymentMethods: IPaymentMethodConfig[],
    operator: string
): IPaymentMethodConfig | null => {
    return paymentMethods?.find((pm) => pm.operator.toUpperCase() === operator.toUpperCase()) ?? null;
};

/**
 * Function to get the user tier config object for a selected payment method.
 * @param {IPaymentMethodConfig} paymentConfig Payment method config object for selected payment method
 * @returns {ITierConfig|null} The plus tier config object or null
 */
export const getTierConfig = (paymentConfig: IPaymentMethodConfig, tierGuid: string): ITierConfig | null => {
    for (const tier of paymentConfig?.tiers || []) {
        if ((tier.tierConfig?.tierGuid || '').toUpperCase() === tierGuid.toUpperCase()) {
            return tier.tierConfig as ITierConfig;
        }
    }
    return null;
};

/**
 * Function to identify if the user subscribed to lagacy tier or not.
 * @param {IUserFullObject} tier tier id
 * @returns {boolean} Returns true in case the user subscribed to lagacy tier.
 */
export const isLegacyTier = (tierGuid = ''): boolean => {
    return (process.env['NX_LEGACY_TIER_GUID'] || NX_LEGACY_TIER_GUID).split(',').includes(tierGuid.toUpperCase());
};

/**
 * Function to identify if the user subscribed to primium tier or not.
 * @param {IUserFullObject} tier tier id
 * @returns {boolean} Returns true in case the user subscribed to primium tier.
 */
export const isPremiumTier = (tierGuid = '') => {
    if (
        [
            getPlusTierGuid()?.toUpperCase(),
            (process.env['NX_LEGACY_PRIMIUM_TIER_GUID'] || NX_LEGACY_PRIMIUM_TIER_GUID)?.toUpperCase(),
        ].includes(tierGuid?.toUpperCase())
    ) {
        return true;
    }
    return false;
};

/**
 * Function to identify if the user subscribed to legacy Classic tier or not.
 * @param {IUserFullObject} tier tier id
 * @returns {boolean} Returns true in case the user subscribed to legacy Classic tier.
 */
export const isLegacyClassicTier = (tierGuid = '') => {
    return (process.env['NX_LEGACY_CLASSIC_TIER_GUID'] || NX_LEGACY_CLASSIC_TIER_GUID)?.toUpperCase() ===
        tierGuid.toUpperCase()
        ? true
        : false;
};

/**
 * Function to identify if the user subscribed to legacy Lite tier or not.
 * @param {IUserFullObject} tier tier id
 * @returns {boolean} Returns true in case the user subscribed to legacy Lite tier.
 */
export const isLegacyLiteTier = (tierGuid = '') => {
    return (process.env['NX_LEGACY_LITE_TIER_GUID'] || NX_LEGACY_LITE_TIER_GUID)?.toUpperCase() ===
        tierGuid.toUpperCase()
        ? true
        : false;
};

/**
 * Function to get subscription GUIDs for BE API filteration.
 * @param {'tvod' | 'rewind' | 'mpx' | 'promotion'} feedType The feed type used for carousel.
 * @param {IUserFullObject} user User object
 * @param {IFreeVsPaidConfig} cmsFVPConfig CMS configuration for free vs paid.
 * @returns {string[]} Array of subscription guids
 */
export const getSubscriptionGuids = (
    feedType: 'tvod' | 'rewind' | 'mpx' | 'promotion' | 'epg',
    user: IUserFullObject,
    cmsFVPConfig: Partial<IFreeVsPaidConfig>
): string[] => {
    const allSubscriptionGuids = user?.subscriptionGUID || [];
    if (!isFreeTier(user)) {
        return allSubscriptionGuids;
    }

    const cmsConfigPaidGuids = cmsFVPConfig.paidSubscriptionGuids;
    return allSubscriptionGuids.concat(cmsConfigPaidGuids || []);
};

/**
 * Gets an array of subscription GUIDs for the provided user.
 * @param user - The user object containing the subscription status.
 * @returns An array of subscription GUIDs for the user.
 */
export const getUserAddonsGuids = (user: IUserFullObject): string[] => {
    return (
        user?.subscriptionStatus
            ?.filter((addon) => isBaseSubscription(addon.subscriptionGuid) === false)
            .map((addon) => addon.subscriptionGuid) || []
    );
};

/**
 * Function to get the FVP operator for the legacy user.
 * @param {IUserFullObject} user User object
 * @returns {string} The operator for the user
 */
export const getOperatorForLegacyUser = (paymentConfig: ICountryConfig): string | null => {
    return paymentConfig?.migrationPaymentMethods?.[0]?.operator ?? null;
};

/**
 * Function to get the migration payment method of the user
 * @param paymentConfig
 * @returns
 */
export const getMigrationPaymentMethod = (paymentConfig: ICountryConfig): IPaymentMethodConfig | null => {
    return paymentConfig?.migrationPaymentMethods?.[0] ?? null;
};

/**
 * Function to get base subscription.
 * @param {IUserFullObject} user User object
 * @returns {object} base subscription object
 */

export const getBaseSubscription = (user: IUserFullObject) => {
    return (
        user?.subscriptionStatus
            ?.filter((sub) => {
                return isBaseSubscription(sub.subscriptionGuid);
            })
            ?.pop() ?? null
    );
};

export const isBaseSubscription = (subscriptionGuid?: string) => {
    return subscriptionGuid ? (subscriptionGuid?.toUpperCase() === 'BASE_SUBSCRIPTION' || subscriptionGuid.search(/BASE_/gi) !== -1) : false;
};

/**
 * Function to get tier for selected operator from country config .
 * @param {string} operator User operator
 *  @param {any} contryConfig contry config
 * @returns {array} tiers array
 */

export const getTierFromCountryConfig = (operator: string, countryConfigs: any) => {
    operator = operator.toLowerCase();
    const tiers =
        (operator &&
            countryConfigs &&
            (Array.isArray(countryConfigs) ? countryConfigs : Object.values(countryConfigs))?.reduce((acc, country) => {
                if (country?.paymentMethods?.length > 0) {
                    for (const paymentMethod of country.paymentMethods) {
                        if (paymentMethod?.operator?.toUpperCase() === operator.toUpperCase() && paymentMethod.tiers) {
                            acc.push(paymentMethod.tiers);
                        }
                    }
                }
                return acc;
            }, [])) ||
        [];

    return tiers;
};

/**
 * Function to check if tiers are available for selected operator on country config .
 * @param {IUserFullObject} user User object
 *  @param {any} contryConfig contry config
 * @returns {boolean}  true if tiers are available
 */

export const isTiersAvailable = (user: IUserFullObject, countryConfig: any) => {
    if (user) {
        const tiers = getTierFromCountryConfig(user?.operator, countryConfig);
        return user.tierStatus && tiers && tiers.length > 0;
    }
    return false;
};

/**
 *  Function to check if user is active or not
 * @param {IUserFullObject} user User object
 * @param {isConsentManagementEnabled} boolean
 * @returns {boolean} Returns true if can delete account.
 */
export const isUserActive = (user: IUserFullObject): boolean => {
    const baseSubscription = getBaseSubscription(user as IUserFullObject);
    if (user?.tierStatus?.active || baseSubscription?.active) {
        return true;
    }
    return false;
};

/**
 * Function to check is addon eligible to unsubscribe
 * @param operator user operator
 * @param eligibleOperators list of all eligible Operators
 * @param packageType  payment type
 * @returns boolean true if eligible else false
 */
export const isEligibleToUnsubscribe = (
    operator: string,
    eligibleOperators: string[],
    paymentType: string
): boolean => {
    if (paymentType === PAYMENT_TYPES.CARD) {
        return true;
    }
    return eligibleOperators.find((opt) => opt?.toUpperCase() === operator?.toUpperCase()) ? true : false;
};

export const isUserActiveAndSuspended = (user: IUserFullObject) => {
    const baseSubscription = getBaseSubscription(user as IUserFullObject);
    const tierStatusName = baseSubscription?.statusName?.toUpperCase();
    if (
        tierStatusName && [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.SUSPENDED].includes(tierStatusName)
    ) {
        return true;
    }
    return false;
}

export const isAddonActiveAndSuspended = (addonStatusName: string) => {
    if (
        addonStatusName && [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.SUSPENDED].includes(addonStatusName?.toUpperCase())
    ) {
        return true;
    }
    return false;
}

export const isUserSuspendedOrTerminated = (user: IUserFullObject) => {
    const baseSubscription = getBaseSubscription(user as IUserFullObject);
    const tierStatusName = baseSubscription?.statusName?.toUpperCase();
    if (
        tierStatusName && [SUBSCRIPTION_STATUS.TERMINATED, SUBSCRIPTION_STATUS.SUSPENDED].includes(tierStatusName)
    ) {
        return true;
    }
    return false;
}

export const isAddonSuspendedOrTerminated = (addonStatusName: string) => {
    if (
        addonStatusName && [SUBSCRIPTION_STATUS.TERMINATED, SUBSCRIPTION_STATUS.SUSPENDED].includes(addonStatusName?.toUpperCase())
    ) {
        return true;
    }
    return false;
}
export const isSubscriptionRenewalFailed = (user: IUserFullObject) => {
    const tierStatus = user?.tierStatus;
    if (
        tierStatus &&
        ((tierStatus?.activity?.toUpperCase() === SUBSCRIPTION_TIER_ACTIVITY.RENEWAL?.toUpperCase() &&
            tierStatus.status === false) ||
            tierStatus?.statusName?.toUpperCase() === SUBSCRIPTION_STATUS.SUSPENDED)
    ) {
        return true;
    }
    return false;
};

export const isSubscriptionRenewalSuccess = (user: IUserFullObject) => {
    const tierStatus = user?.tierStatus;
    if (
        tierStatus &&
        tierStatus?.activity?.toUpperCase() === SUBSCRIPTION_TIER_ACTIVITY.RENEWAL?.toUpperCase() &&
        tierStatus.status === true
    ) {
        return true;
    }
    return false;
};

export const isTierSubscriptionSuccess = (user: IUserFullObject) => {
    const tierStatus = user?.tierStatus;
    //note:  this condition for free active user also so need to check with tierguid also
    if (
        tierStatus &&
        tierStatus?.activity?.toUpperCase() === SUBSCRIPTION_TIER_ACTIVITY.SUBSCRIPTION?.toUpperCase() &&
        tierStatus.status === true
    ) {
        return true;
    }
    return false;
};

export const isTierUnSubscribedAndActive = (user: IUserFullObject) => {
    const tierStatus = user?.tierStatus;
    if (
        tierStatus &&
        tierStatus?.activity?.toUpperCase() === SUBSCRIPTION_TIER_ACTIVITY.UNSUBSCRIPTION?.toUpperCase() &&
        // tierStatus.active === true
        tierStatus?.nextRenewalDate &&
        isBeforeOrEqualDate(tierStatus?.nextRenewalDate)
    ) {
        return true;
    }
    return false;
};

/**
 * Function to identify if the user has inaap operator .
 * @param {IUserFullObject} operator operator
 * @returns {boolean} return true in case the user has inapp operator
 */
export const isInAppOperator = (operator = '', allowedOperators?: string[]): boolean => {
    if (allowedOperators && allowedOperators?.length > 0) {
        return allowedOperators?.find((opt) => opt?.toUpperCase() === operator?.toUpperCase()) ? true : false
    }
    return operator?.toUpperCase().includes('IAPP_');
};

/**
 * Function to identify if the user  has inaap android .
 * @param {IUserFullObject} operator tier id
 * @returns {boolean} Returns true user is android app operator.
 */
export const isInAppAndroid = (operator = '', allowedOperators?: string[]): boolean => {
    const operatorsList = allowedOperators && allowedOperators?.length > 0 ? allowedOperators : (process.env['NX_IN_APP_ANDROID_OPERATORS'] || NX_IN_APP_ANDROID_OPERATORS).split(',')
    return operatorsList?.find((opt) => opt?.toUpperCase() === operator?.toUpperCase()) ? true : false
};

/**
 * Function to identify if the user  has inaap apple .
 * @param {IUserFullObject} operator tier id
 * @returns {boolean} Returns true user is apple app operator.
 */
export const isInAppApple = (operator = '', allowedOperators?: string[]): boolean => {
    const operatorsList = allowedOperators && allowedOperators?.length > 0 ? allowedOperators : (process.env['NX_IN_APP_APPLE_OPERATORS'] || NX_IN_APP_APPLE_OPERATORS).split(',')
    return operatorsList?.find((opt) => opt?.toUpperCase() === operator?.toUpperCase()) ? true : false

};

export const isFreeTrialConsumed = (user: IUserFullObject): boolean => {
    return user?.tierStatus?.freeTrialConsumed ?? false;
};

export const isGracePeriodActive = (user: IUserFullObject): boolean => {
    return user?.tierStatus?.isGracePeriod ?? false;
};

